// src/PrivacyPolicy.jsx
import React from 'react';
import NavigationBar from './NavigationBar';
import './PolicyPages.css';

const PrivacyPolicy = () => {
  return (
    <>
      <NavigationBar />
      <div className="policy-page-container">
        <div className="policy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2>1. Introduction</h2>
            <p>
              TickrTrends ("we," "our," or "us") respects your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and protect your information when you use our data aggregation 
              and analysis platform. By using the Service, you consent to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
              <li><strong>Payment Information:</strong> Processed and stored by Stripe (we do NOT store complete credit card numbers)</li>
              <li><strong>Communications:</strong> Messages you send to our support team</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages viewed, features accessed, time spent on platform, search queries</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, location data</li>
              <li><strong>Cookies and Tracking:</strong> Session data, preferences, analytics information</li>
            </ul>

            <h3>2.3 Third-Party Data</h3>
            <p>
              We aggregate publicly available financial data from third-party sources (Yahoo Finance, financial APIs) 
              for analysis purposes. This data is not personal information.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide, operate, and maintain the Service</li>
              <li>Process your subscription payments and renewals</li>
              <li>Authenticate your identity and prevent fraud</li>
              <li>Send service-related communications (billing, updates, security alerts)</li>
              <li>Respond to your support requests and inquiries</li>
              <li>Analyze usage patterns to improve the Service</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Generate anonymized, aggregated analytics (no personal identification)</li>
            </ul>
            <p>
              <strong>We do NOT:</strong>
            </p>
            <ul>
              <li>Sell your personal information to third parties</li>
              <li>Use your data to provide personalized investment advice</li>
              <li>Share your investment activity with others</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            
            <h3>4.1 Service Providers</h3>
            <p>We share information with trusted third-party service providers who assist us:</p>
            <ul>
              <li><strong>Stripe:</strong> Payment processing (PCI-DSS compliant)</li>
              <li><strong>AWS:</strong> Cloud hosting, data storage, and authentication (AWS Cognito)</li>
              <li><strong>Analytics Providers:</strong> Usage analytics and performance monitoring</li>
            </ul>
            <p>
              These providers are contractually obligated to protect your information and use it only for specified purposes.
            </p>

            <h3>4.2 Legal Requirements</h3>
            <p>We may disclose your information if required to:</p>
            <ul>
              <li>Comply with legal obligations (subpoenas, court orders, regulatory requests)</li>
              <li>Protect our rights, property, or safety</li>
              <li>Prevent fraud, security threats, or illegal activity</li>
              <li>Enforce our Terms of Service</li>
            </ul>

            <h3>4.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, bankruptcy, or sale of assets, your information 
              may be transferred to the acquiring entity. We will notify you of such changes.
            </p>

            <h3>4.4 With Your Consent</h3>
            <p>
              We may share information for other purposes with your explicit consent.
            </p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement industry-standard security measures including:
            </p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
              <li>Secure authentication via AWS Cognito</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and employee training</li>
              <li>Secure data storage on AWS infrastructure</li>
            </ul>
            <p>
              <strong>However:</strong> No method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your information, we cannot guarantee absolute security. 
              You use the Service at your own risk.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your information for as long as:
            </p>
            <ul>
              <li>Your account is active</li>
              <li>Necessary to provide the Service</li>
              <li>Required for legal, tax, or accounting purposes (typically 7 years)</li>
              <li>Needed to resolve disputes or enforce agreements</li>
            </ul>
            <p>
              Upon account deletion, we will delete or anonymize your personal information within 90 days, 
              except where retention is required by law.
            </p>
          </section>

          <section>
            <h2>7. Your Privacy Rights</h2>
            
            <h3>7.1 General Rights</h3>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your information</li>
              <li><strong>Restriction:</strong> Request limitation on how we use your data</li>
            </ul>

            <h3>7.2 California Residents (CCPA)</h3>
            <p>
              California residents have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information (we do not sell data)</li>
              <li>Right to non-discrimination for exercising CCPA rights</li>
            </ul>

            <h3>7.3 European Residents (GDPR)</h3>
            <p>
              If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR), 
              including the rights listed above plus the right to withdraw consent and lodge complaints with supervisory authorities.
            </p>

            <h3>7.4 Exercising Your Rights</h3>
            <p>
              To exercise any privacy rights, contact us at <strong>ryan@tickrtrends.com</strong>. 
              We will respond within 30 days. You may need to verify your identity.
            </p>
          </section>

          <section>
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies, web beacons, and similar technologies to:
            </p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Authenticate your session</li>
              <li>Analyze usage patterns and improve performance</li>
              <li>Understand which features are most popular</li>
            </ul>
            <p>
              You can control cookies through your browser settings. However, disabling cookies may 
              limit functionality of the Service.
            </p>
          </section>

          <section>
            <h2>9. Third-Party Services and Links</h2>
            <p>
              Our Service integrates with third-party services:
            </p>
            <ul>
              <li><strong>Stripe:</strong> Payment processing - <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              <li><strong>AWS:</strong> Cloud infrastructure - <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            </ul>
            <p>
              We are not responsible for the privacy practices of third-party websites or services. 
              We encourage you to review their privacy policies.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              The Service is NOT intended for individuals under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe a child has provided us with 
              personal information, contact us immediately at ryan@tickrtrends.com and we will delete it.
            </p>
          </section>

          <section>
            <h2>11. Do Not Track</h2>
            <p>
              Some browsers include a "Do Not Track" (DNT) feature. Our Service does not currently 
              respond to DNT signals due to lack of industry standards.
            </p>
          </section>

          <section>
            <h2>12. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in the United States or other countries 
              where our service providers operate. By using the Service, you consent to such transfers. 
              We ensure appropriate safeguards are in place for international transfers.
            </p>
          </section>

          <section>
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. Material changes will be communicated via:
            </p>
            <ul>
              <li>Email notification to your registered address</li>
              <li>Prominent notice on the Service</li>
              <li>Updated "Last Updated" date at the top of this policy</li>
            </ul>
            <p>
              Your continued use after changes constitutes acceptance. We encourage periodic review of this policy.
            </p>
          </section>

          <section>
            <h2>14. Contact Us</h2>
            <p>
              Questions about this Privacy Policy or our data practices? Contact us at:
              <br />
              <strong>Email:</strong> ryan@tickrtrends.com
              <br />
              <strong>Response Time:</strong> Within 30 days
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;